// ReceiptPrintService.js
import moment from 'moment';
import ApiService from './apiService';

class ReceiptPrintService {
  constructor() {
    // ✅ Constructor stays sync — no async calls here
    this.restaurantInfo = null;
    this.printerConfig = this.loadPrinterConfig();
  }

  // ✅ Fetches VAT number from API
  async getvatNumberFromApi() {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_TAX_SETTING');
      // handle both Axios-wrapped (res.data.result) and unwrapped (res.result)
      return res?.data?.result?.vatNumber || res?.result?.vatNumber || '';
    } catch (e) {
      console.error('Failed to fetch VAT number from API', e);
      return '';
    }
  }

  // ✅ Always awaitable — reads localStorage + API, returns resolved plain object
  async getRestaurantInfo() {
    const fallback = {
      name: 'Xtream Nutrition',
      city: 'South Africa',
      phone: '+971 XX XXX XXXX',
      taxId: 'TRN: 100000000000000',
      email: 'xtremenutrition.co.za',
      website: 'www.xtremenutrition.co.za',
      logo: '',
    };

    try {
      const rawCompanies = localStorage.getItem('userCompanies');
      const userCompanies = rawCompanies ? JSON.parse(rawCompanies) : [];

      // ✅ await so we get the string value, not a Promise
      const vatNumber = await this.getvatNumberFromApi();

      if (!Array.isArray(userCompanies) || !userCompanies.length) {
        return {
          ...fallback,
          taxId: vatNumber ? `VAT No: ${vatNumber}` : fallback.taxId,
        };
      }

      const selectedCompanyNo = localStorage.getItem('selectedCompanyNo');
      const active = (selectedCompanyNo ? userCompanies.find((c) => String(c?.companyNo) === String(selectedCompanyNo)) : null) || userCompanies[0];

      return {
        ...fallback,
        name: active?.companyName || fallback.name,
        logo: active?.logo || fallback.logo,
        city: active?.city || active?.addressCity || active?.country || fallback.city,
        phone: active?.phone || active?.mobile || active?.tel || fallback.phone,
        // ✅ API vatNumber takes priority, then company fields, then fallback
        taxId: vatNumber ? `VAT No: ${vatNumber}` : active?.taxId || active?.trn || active?.vatNo || active?.vatNumber || fallback.taxId,
        email: active?.email || active?.companyEmail || fallback.email,
        website: active?.website || active?.web || fallback.website,
      };
    } catch (e) {
      return fallback;
    }
  }

  // ------------------------- PRINT FRAME -------------------------
  getOrCreatePrintFrame() {
    let frame = document.getElementById('posPrintFrame');
    if (!frame) {
      frame = document.createElement('iframe');
      frame.id = 'posPrintFrame';
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.border = '0';
      document.body.appendChild(frame);
    }
    return frame;
  }

  async printHtmlInFrame(html) {
    const frame = this.getOrCreatePrintFrame();
    const win = frame.contentWindow;
    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();
    await new Promise((r) => setTimeout(r, 150));
    win.focus();
    win.print();
    await new Promise((r) => setTimeout(r, 400));
  }

  // ------------------------- HELPERS -------------------------
  round2(n) {
    const x = Number(n);
    return Number.isFinite(x) ? Math.round(x * 100) / 100 : 0;
  }

  getPayments(orderData) {
    if (Array.isArray(orderData?.payments) && orderData.payments.length) {
      return orderData.payments
        .map((p) => ({ method: String(p?.method || '').trim(), amount: this.round2(p?.amount) }))
        .filter((p) => p.method && p.amount > 0);
    }
    const m = String(orderData?.paymentMethod || '').trim();
    const a = this.round2(orderData?.amountPaid);
    return m && a > 0 ? [{ method: m, amount: a }] : [];
  }

  sumPayments(payments) {
    return this.round2(payments.reduce((s, p) => s + this.round2(p.amount), 0));
  }

  extractBarcodes(input) {
    const out = [];
    const push = (v) => {
      const s = (v ?? '').toString().trim();
      if (s) out.push(s);
    };
    const dig = (obj) => {
      if (!obj) return;
      push(obj.barcodeToPrint);
      push(obj.updateBarcode);
      push(obj.updatedBarcode);
      push(obj.newBarcode);
      push(obj.newGiftCardBarcode);
      push(obj.barcode);
      if (obj.result) dig(obj.result);
      if (obj.order) dig(obj.order);
      if (obj.data) dig(obj.data);
      if (Array.isArray(obj.barcodes)) obj.barcodes.forEach(push);
      if (Array.isArray(obj.barcodeList)) obj.barcodeList.forEach(push);
      const recArrays = [obj.orderedGiftCardRecords, obj.giftCardRecords, obj.records, obj.giftCardRedemptions].filter(Array.isArray);
      recArrays.forEach((arr) => {
        arr.forEach((r) => {
          push(r?.updateBarcode);
          push(r?.newBarcode);
          push(r?.barcode);
        });
      });
      if (Array.isArray(obj.orderItems)) {
        obj.orderItems.forEach((it) => {
          push(it?.updateBarcode);
          push(it?.newBarcode);
          push(it?.barcode);
        });
      }
    };
    dig(input);
    return [...new Set(out)];
  }

  getPrimaryBarcode(orderData) {
    const barcodes = this.extractBarcodes(orderData);
    return barcodes.length ? barcodes[0] : '';
  }

  // ------------------------- CONFIG -------------------------
  loadPrinterConfig() {
    const saved = localStorage.getItem('printerConfig');
    if (saved) return JSON.parse(saved);
    const defaultConfig = {
      paperWidth: 80,
      enableAutoprint: true,
      copies: 1,
      showPreview: false,
      printLogo: true,
      printBarcode: true,
      printQRCode: false,
      useNetworkPrinter: true,
      printerIp: '192.168.18.123:9100',
    };
    localStorage.setItem('printerConfig', JSON.stringify(defaultConfig));
    return defaultConfig;
  }

  savePrinterConfig(config) {
    this.printerConfig = { ...this.printerConfig, ...config };
    localStorage.setItem('printerConfig', JSON.stringify(this.printerConfig));
  }

  hasNetworkPrinter() {
    return !!(this.printerConfig && this.printerConfig.printerIp);
  }

  resolvePrinterUrl() {
    if (!this.hasNetworkPrinter()) return null;
    let target = String(this.printerConfig.printerIp).trim();
    if (!target.startsWith('http://') && !target.startsWith('https://')) target = `http://${target}`;
    if (!target.endsWith('/')) target += '/';
    return target;
  }

  // ---------------------- PUBLIC API ------------------------
  async printReceipt(orderData, currency) {
    // ✅ Always await getRestaurantInfo() — gives a real resolved object
    this.restaurantInfo = await this.getRestaurantInfo();

    const merged = {
      ...orderData,
      barcodeToPrint: (orderData?.barcodeToPrint ?? '').toString().trim() || this.getPrimaryBarcode(orderData),
    };

    const html = this.generateReceiptHTML(merged, currency);

    if (this.hasNetworkPrinter() && this.printerConfig.useNetworkPrinter) {
      const escPosData = this.buildEscPosReceipt(merged, currency);
      const net = await this.sendToNetworkPrinterWithTimeout(escPosData, 1500);
      if (net.ok) {
        if (!this.printerConfig.showPreview) return { success: true, method: 'network' };
      } else {
        console.warn('Network print failed, falling back to browser', net);
      }
    }

    if (this.printerConfig.showPreview) return this.printWithPreview(html);
    return this.printDirectly(html);
  }

  async printVoucher(orderData, currency) {
    // ✅ await here too — same fix as printReceipt
    this.restaurantInfo = await this.getRestaurantInfo();

    const html = this.generateVoucherHTML(orderData, currency);
    await this.printHtmlInFrame(html);
    return { success: true, method: 'iframe' };
  }

  async openCashDrawer() {
    if (!this.hasNetworkPrinter()) throw new Error('No network printer IP configured, cannot open cash drawer');
    const drawerPulse = '\x1B\x70\x00\x32\xFA';
    const res = await this.sendToNetworkPrinterWithTimeout(drawerPulse, 1500);
    if (!res.ok) throw new Error('Printer did not accept drawer command');
    return { success: true };
  }

  // ---------------------- NETWORK PRINT ---------------------
  async sendToNetworkPrinter(rawData) {
    const url = this.resolvePrinterUrl();
    if (!url) return { ok: false, reason: 'no-ip' };
    try {
      await fetch(url, { method: 'POST', mode: 'no-cors', body: rawData });
      return { ok: true };
    } catch (error) {
      console.error('sendToNetworkPrinter error:', error);
      return { ok: false, error };
    }
  }

  async sendToNetworkPrinterWithTimeout(rawData, timeoutMs = 1500) {
    const url = this.resolvePrinterUrl();
    if (!url) return { ok: false, reason: 'no-ip' };
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);
    try {
      await fetch(url, { method: 'POST', mode: 'no-cors', body: rawData, signal: controller.signal });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || e };
    } finally {
      clearTimeout(t);
    }
  }

  // ---------------------- ESC/POS RECEIPT ---------------------
  buildEscPosReceipt(orderData, currency) {
    const {
      orderId,
      orderDate,
      orderItems = [],
      subtotal = 0,
      tax = 0,
      discount = 0,
      total = 0,
      customerName,
      orderType,
      invoiceNumber,
      tableNumber,
    } = orderData;
    const currencyCode = currency?.code || 'AED';
    const padRight = (txt = '', len = 22) => String(txt).slice(0, len).padEnd(len, ' ');
    const padLeft = (txt = '', len = 10) => String(txt).slice(0, len).padStart(len, ' ');
    const payments = this.getPayments(orderData);
    const paidTotal = this.sumPayments(payments);
    const computedChange = this.round2(Math.max(0, paidTotal - this.round2(total)));

    let out = '';
    out += '\x1B@';
    out += '\x1B!\x18';
    out += `${this.restaurantInfo.name}\n`;
    out += '\x1B!\x00';
    out += `${this.restaurantInfo.city}\n`;
    out += `Tel: ${this.restaurantInfo.phone}\n`;
    out += `${this.restaurantInfo.taxId}\n`;
    out += `${this.restaurantInfo.email}\n`;
    out += '\n';
    out += `Order: ORD-${orderId}\n`;
    out += `Invoice: ${invoiceNumber}\n`;
    out += `Date: ${moment(orderDate).format('DD/MM/YYYY')} ${moment(orderDate).format('HH:mm:ss')}\n`;
    out += `Type: ${orderType || ''}\n`;
    if (tableNumber) out += `Table: ${tableNumber}\n`;
    out += `Customer: ${customerName || 'Walk-in Customer'}\n`;
    out += '--------------------------------\n';
    out += 'Items\n';
    out += '--------------------------------\n';

    orderItems.forEach((item) => {
      out += `${item.itemName}\n`;
      if (item.selectedVariant) out += `  (${item.selectedVariant})\n`;
      const qtyPrice = `${item.quantity} x ${currencyCode} ${Number(item.menuItemPrice || 0).toFixed(2)}`;
      const lineTotal = `${currencyCode} ${(Number(item.quantity || 0) * Number(item.menuItemPrice || 0)).toFixed(2)}`;
      out += `${padRight(qtyPrice, 22)}${padLeft(lineTotal, 10)}\n`;
      if (item.specialInstructions) out += `  Note: ${item.specialInstructions}\n`;
    });

    out += '--------------------------------\n';
    out += `${padRight('Subtotal:', 22)}${padLeft(`${currencyCode} ${Number(subtotal).toFixed(2)}`, 10)}\n`;
    if (Number(tax) > 0) out += `${padRight('Tax:', 22)}${padLeft(`${currencyCode} ${Number(tax).toFixed(2)}`, 10)}\n`;
    if (Number(discount) > 0) out += `${padRight('Discount:', 22)}${padLeft(`-${currencyCode} ${Number(discount).toFixed(2)}`, 10)}\n`;
    out += `${padRight('TOTAL:', 22)}${padLeft(`${currencyCode} ${Number(total).toFixed(2)}`, 10)}\n`;
    out += '--------------------------------\n';
    out += 'Payment\n';
    out += '--------------------------------\n';
    payments.forEach((p) => {
      out += `${padRight(`${p.method}:`, 22)}${padLeft(`${currencyCode} ${Number(p.amount).toFixed(2)}`, 10)}\n`;
    });
    out += '--------------------------------\n';
    out += `${padRight('Paid Total:', 22)}${padLeft(`${currencyCode} ${paidTotal.toFixed(2)}`, 10)}\n`;
    if (computedChange > 0) out += `${padRight('Change:', 22)}${padLeft(`${currencyCode} ${computedChange.toFixed(2)}`, 10)}\n`;

    const b = String(orderData?.barcodeToPrint || '').trim();
    if (this.printerConfig.printBarcode && b) {
      out += '--------------------------------\n';
      out += `GiftCard Barcode: ${b}\n`;
    }

    out += '\nThank you for your business!\n';
    out += 'Powered by Pro360 ERP\n';
    out += '\n\n\n';
    out += '\x1D\x56\x00';
    return out;
  }

  buildEscPosVoucher(orderData, currency) {
    const currencyCode = currency?.code || '-';
    const b = (orderData?.barcodeToPrint || '').toString().trim() || this.getPrimaryBarcode(orderData);
    const giftCardCode = (orderData?.giftCardCodeToPrint || '').toString().trim();
    const title = (orderData?.voucherTitle || 'VOUCHER').toString();

    let out = '';
    out += '\x1B@';
    out += '\x1B!\x18';
    out += `${title}\n`;
    out += '\x1B!\x00';
    out += '--------------------------------\n';

    const voucherValue = Number(orderData?.voucherValue ?? orderData?.total ?? 0) || 0;
    out += `Value: ${currencyCode} ${voucherValue.toFixed(2)}\n`;
    out += '--------------------------------\n';

    if (b) {
      out += `BARCODE:\n${b}\n`;
      if (giftCardCode) out += `CODE: ${giftCardCode}\n`;
    } else {
      out += '(No barcode returned)\n';
    }

    out += '\n\n\n';
    out += '\x1D\x56\x00';
    return out;
  }

  // ---------------------- HTML RECEIPT ----------------------
  generateReceiptHTML(orderData, currency) {
    const {
      orderId,
      orderDate,
      orderItems = [],
      subtotal = 0,
      tax = 0,
      discount = 0,
      total = 0,
      customerName,
      orderType,
      invoiceNumber,
      tableNumber,
    } = orderData;
    const payments = this.getPayments(orderData);
    const paidTotal = this.sumPayments(payments);
    const computedChange = this.round2(Math.max(0, paidTotal - this.round2(total)));
    const currencyCode = currency?.code || 'AED';
    const barcodeValue = (orderData?.barcodeToPrint || '').toString().trim() || this.getPrimaryBarcode(orderData);
    const safeBarcodeJson = JSON.stringify(barcodeValue || '');
    debugger;
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt #${orderId}</title>
  <style>
    @media print {
      @page { margin: 0; size: ${this.printerConfig.paperWidth}mm auto; }
      body { margin: 0; padding: 0; }
      .no-print { display: none !important; }
    }
    * { box-sizing: border-box; }
    body {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.4;
      width: ${this.printerConfig.paperWidth}mm;
      margin: 0 auto;
      padding: 10px;
      background: #fff;
      color: #000;
    }
    .header { text-align: center; margin-bottom: 10px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
    .company-name { font-size: 18px; font-weight: 700; }
    .company-info { font-size: 10px; }
    .section { margin: 10px 0; border-bottom: 1px dashed #000; padding-bottom: 10px; }
    .row { display: flex; justify-content: space-between; margin: 4px 0; }
    .items-title { font-weight: 700; border-bottom: 1px solid #000; padding-bottom: 6px; margin-bottom: 6px; }
    .item { border-bottom: 1px dotted #ccc; padding: 6px 0; }
    .item-name { font-weight: 700; font-size: 11px; }
    .item-sub { display: flex; justify-content: space-between; font-size: 10px; color: #333; }
    .total { font-size: 14px; font-weight: 800; border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 8px 0; margin: 10px 0; }
    .barcode-wrap { text-align: center; margin: 14px 0; }
    svg#barcodeSvg { width: 100%; height: auto; }
    .footer { text-align: center; margin-top: 12px; font-size: 10px; border-top: 1px dashed #000; padding-top: 10px; }
    .no-print { margin: 16px 0; text-align: center; }
    .btn { padding: 10px 16px; border-radius: 6px; border: none; cursor: pointer; }
    .btn-print { background: #6e5199; color: #fff; }
    .btn-close { background: #6c757d; color: #fff; margin-left: 8px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">${this.restaurantInfo.name}</div>
    <div class="company-info">
      ${this.restaurantInfo.city}<br/>
      Tel: ${this.restaurantInfo.phone}<br/>
      ${this.restaurantInfo.taxId}<br/>
      ${this.restaurantInfo.email}
    </div>
  </div>

  <div class="section">
    <div class="row"><b>Order #:</b><span>ORD-${orderId}</span></div>
    <div class="row"><b>Invoice #:</b><span>${invoiceNumber}</span></div>
    <div class="row"><b>Date:</b><span>${moment(orderDate).format('DD/MM/YYYY')}</span></div>
    <div class="row"><b>Time:</b><span>${moment(orderDate).format('HH:mm:ss')}</span></div>
    <div class="row"><b>Type:</b><span>${orderType || ''}</span></div>
    ${tableNumber ? `<div class="row"><b>Table:</b><span>${tableNumber}</span></div>` : ''}
    <div class="row"><b>Customer:</b><span>${customerName || 'Walk-in Customer'}</span></div>
  </div>

  <div class="items-title">ITEMS</div>
  ${orderItems
    .map(
      (it, idx) => `
    <div class="item">
      <div class="item-name">${idx + 1}. ${it.itemName || ''}</div>
      ${it.selectedVariant ? `<div style="font-size:10px;color:#666;font-style:italic;margin-left:10px;">${it.selectedVariant}</div>` : ''}
      <div class="item-sub">
        <span>${it.quantity} x ${currencyCode} ${Number(it.menuItemPrice || 0).toFixed(2)}</span>
        <span>${currencyCode} ${(Number(it.quantity || 0) * Number(it.menuItemPrice || 0)).toFixed(2)}</span>
      </div>
      ${it.specialInstructions ? `<div style="font-size:9px;color:#666;margin-left:10px;">📝 ${it.specialInstructions}</div>` : ''}
    </div>
  `,
    )
    .join('')}

  <div class="section">
    <div class="row"><span>Subtotal:</span><span>${currencyCode} ${Number(subtotal).toFixed(2)}</span></div>
    ${Number(discount) > 0 ? `<div class="row" style="color:#0a7a2f;"><span>Discount:</span><span>-${currencyCode} ${Number(discount).toFixed(2)}</span></div>` : ''}
    <div class="row total"><span>TOTAL (Incl. VAT):</span><span>${currencyCode} ${Number(total).toFixed(2)}</span></div>

  </div>

  <div class="section">
    <div class="items-title">PAYMENT</div>
    ${payments
      .map(
        (p) => `
      <div class="row"><b>${p.method}:</b><span>${currencyCode} ${Number(p.amount).toFixed(2)}</span></div>
    `,
      )
      .join('')}
    <div class="row"><b>Paid Total:</b><span>${currencyCode} ${paidTotal.toFixed(2)}</span></div>
    ${computedChange > 0 ? `<div class="row"><b>Change:</b><span>${currencyCode} ${computedChange.toFixed(2)}</span></div>` : ''}
    
        ${
          Number(tax) > 0
            ? `
    <div class="row" style="font-size:11px;color:#555;margin-top:4px;">
      <span>VAT Amount:</span><span>${currencyCode} ${Number(tax).toFixed(2)}</span>
    </div>
    <div class="row" style="font-size:11px;color:#555;">
      <span>Ex VAT Total:</span><span>${currencyCode} ${(Number(total) - Number(tax)).toFixed(2)}</span>
    </div>`
            : ''
        }
  </div>

  ${
    this.printerConfig.printBarcode
      ? `
    <div class="barcode-wrap">
      <div style="font-weight:800;margin-bottom:6px;">BARCODE</div>
      ${
        barcodeValue
          ? `<svg id="barcodeSvg"></svg><div style="font-size:11px;margin-top:6px;">${barcodeValue}</div>`
          : `<div style="font-size:11px;">(No barcode returned)</div>`
      }
    </div>
  `
      : ''
  }

  <div class="footer">
    <div style="font-weight:800;">✨ Thank you for your business! ✨</div>
    <div>We appreciate your visit</div>
    <div>Please come again</div>
    <div style="margin-top: 8px; font-size: 9px;">
      ${this.restaurantInfo.website}<br/>
      Powered by Pro360 ERP System
    </div>
  </div>

  <div class="no-print">
    <button class="btn btn-print" onclick="window.print()">🖨️ Print Receipt</button>
    <button class="btn btn-close" onclick="window.close()">Close</button>
  </div>

  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
  <script>
    (function(){
      var v = ${safeBarcodeJson};
      if (!v) return;
      try {
        if (window.JsBarcode) {
          JsBarcode("#barcodeSvg", v, { format: "CODE128", displayValue: false, height: 60, margin: 10 });
        }
      } catch(e) { console.error("Barcode render failed", e); }
    })();
  </script>
</body>
</html>`;
  }

  // ---------------------- GIFT CARD VOUCHER ----------------------
  getGiftCardVoucherValue(orderData) {
    const items = Array.isArray(orderData?.orderItems) ? orderData.orderItems : [];
    const giftItems = items.filter((it) => it?.isGiftCard === true);
    const value = giftItems.reduce((sum, it) => {
      return sum + Number(it?.quantity || 0) * Number(it?.menuItemPrice || 0);
    }, 0);
    return this.round2(value);
  }

  generateVoucherHTML(orderData, currency) {
    const currencyCode = currency?.code || 'AED';
    const barcodeValue = (orderData?.barcodeToPrint || '').toString().trim() || this.getPrimaryBarcode(orderData);
    const giftCardCode = (orderData?.giftCardCodeToPrint || '').toString().trim();
    const title = (orderData?.voucherTitle || 'VOUCHER').toString();
    const giftCardValue = this.getGiftCardVoucherValue(orderData);
    const safeBarcodeJson = JSON.stringify(barcodeValue || '');
    const isRedeem =
      String(orderData?.voucherMode || '')
        .trim()
        .toUpperCase() === 'REDEEM' ||
      Number(orderData?.voucherRemainingValue || 0) > 0 ||
      Number(orderData?.voucherRedeemedValue || 0) > 0;

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    @media print {
      @page { margin: 0; size: ${this.printerConfig.paperWidth}mm auto; }
      body { margin: 0; padding: 0; }
    }
    body {
      font-family: 'Courier New', monospace;
      width: ${this.printerConfig.paperWidth}mm;
      margin: 0 auto;
      padding: 10px;
      background: #fff;
      color: #000;
    }
    .box { border: 2px dashed #000; padding: 10px; }
    .center { text-align: center; }
    .title { font-size: 18px; font-weight: 900; margin-bottom: 6px; }
    .sub { font-size: 11px; margin: 2px 0; }
    .value { font-size: 18px; font-weight: 900; margin: 10px 0; padding: 8px 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; }
    .barcode-wrap { margin-top: 10px; }
    svg#voucherBarcodeSvg { width: 100%; height: auto; }
    .code { font-size: 12px; font-weight: 800; margin-top: 6px; }
    .small { font-size: 10px; }
  </style>
</head>
<body>
  <div class="box">
    <div class="center title">${title}</div>
    <div class="center value">
      CARD VALUE: ${currencyCode} ${isRedeem ? Number(orderData?.voucherRemainingValue || 0).toFixed(2) : giftCardValue.toFixed(2)}
    </div>
    <div class="barcode-wrap center">
      ${
        barcodeValue
          ? `<svg id="voucherBarcodeSvg"></svg>
           <div class="code">${barcodeValue}</div>
           ${giftCardCode ? `<div class="small">Code: <b>${giftCardCode}</b></div>` : ''}`
          : `<div class="small"><b>(No barcode returned)</b></div>`
      }
    </div>
    <div class="center small" style="margin-top:10px;">
      Please present this voucher at redemption.
    </div>
  </div>

  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
  <script>
    (function(){
      var v = ${safeBarcodeJson};
      if (!v) return;
      try {
        if (window.JsBarcode) {
          JsBarcode("#voucherBarcodeSvg", v, { format: "CODE128", displayValue: false, height: 70, margin: 12 });
        }
      } catch(e) { console.error("Voucher barcode render failed", e); }
    })();
  </script>
</body>
</html>`;
  }

  // ---------------------- BROWSER PRINT ---------------------
  async printDirectly(html) {
    const printWindow = window.open('', '_blank', 'width=400,height=700,scrollbars=yes');
    if (!printWindow) throw new Error('Pop-up blocked. Please allow pop-ups for printing.');
    printWindow.document.write(html);
    printWindow.document.close();
    return new Promise((resolve) => {
      printWindow.onload = () => {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          setTimeout(() => {
            printWindow.close();
            resolve({ success: true, method: 'browser' });
          }, 800);
        }, 300);
      };
    });
  }

  async printWithPreview(html) {
    const printWindow = window.open('', '_blank', 'width=450,height=750,scrollbars=yes');
    if (!printWindow) throw new Error('Pop-up blocked. Please allow pop-ups for printing.');
    printWindow.document.write(html);
    printWindow.document.close();
    return { success: true, method: 'browser-preview' };
  }
}

export default new ReceiptPrintService();
